import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import * as echarts from 'echarts';
import { ChartType } from '../../../../API';
import { FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { DataArrays } from '../../models/charts';
import { COLOR_PALETTES, US_STATE_MAPPINGS } from '../../models/chart-data';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'app-chart-preview',
  templateUrl: './chart-preview.component.html',
  styleUrl: './chart-preview.component.scss',
})
export class ChartPreviewComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('barChart', { static: false }) barChartRef!: ElementRef;
  @ViewChild('lineChart', { static: false }) lineChartRef!: ElementRef;
  @ViewChild('scatterChart', { static: false }) scatterChartRef!: ElementRef;
  @ViewChild('pieChart', { static: false }) pieChartRef!: ElementRef;
  @ViewChild('geoChart', { static: false }) geoChartRef!: ElementRef;

  @Input() type?: string;
  @Input() configForm?: FormGroup;
  @Input() chartPreviewData?: DataArrays;
  @Input() displayConfig: any;
  @Input() hideHeader?: boolean = false;
  @Input() isDashboardPreview?: boolean = false;
  @Output() chartSaved = new EventEmitter<string>();

  temp: { height: number; width: number } = { height: 0, width: 0 };

  @Input() set parentSize({ height, width }: { height: number; width: number }) {
    if (height !== this.temp.height || width !== this.temp.width) {
      this.temp.width = width;
      this.temp.height = height;
      this.resizeCharts();
    }
  }

  barChartInstance!: echarts.ECharts;
  lineChartInstance!: echarts.ECharts;
  scatterChartInstance!: echarts.ECharts;
  pieChartInstance!: echarts.ECharts;
  geoChartInstance!: echarts.ECharts;

  constructor(private nzMessageService: NzMessageService) {}

  ngOnInit() {
    if (this.chartPreviewData && this.configForm?.valid) {
      this.displayConfig = this.configForm.value;
    }

    this.configForm?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((newValue) => {
        this.displayConfig = newValue;
        this.renderChart();
      });
  }

  ngAfterViewInit(): void {
    if (this.displayConfig) {
      this.renderChart();
    }
    // Resize on window resize
    window.addEventListener('resize', this.resizeCharts);
  }

  ngOnDestroy(): void {
    this.barChartInstance?.dispose();
    this.lineChartInstance?.dispose();
    this.scatterChartInstance?.dispose();
    this.pieChartInstance?.dispose();
    this.geoChartInstance?.dispose();

    window.removeEventListener('resize', this.resizeCharts);
  }

  initBarChart() {
    this.barChartInstance = echarts.init(this.barChartRef.nativeElement, null, { renderer: 'svg' });
    const barOptions: echarts.EChartsOption = {
      title: { text: this.displayConfig.chartTitle, left: 'center' },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: this.chartPreviewData![this.displayConfig.xAxisValue],
        name: this.displayConfig.xAxisLabel,
        nameLocation: 'middle',
        nameGap: this.xAxisFormatter(),
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 14,
          color: '#333',
        },
        splitLine: {
          show: this.displayConfig.xAxisGridlines,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          interval: 0,
          rotate: 30,
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        name: this.displayConfig.yAxisLabel,
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 14,
          color: '#333',
        },
        axisLabel: {
          formatter: this.numberFormatter,
        },
        splitLine: {
          show: this.displayConfig.yAxisGridlines,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
      },
      series: [
        {
          name: this.displayConfig.yAxisValue,
          type: 'bar',
          data: this.chartPreviewData![this.displayConfig.yAxisValue],
          itemStyle: {
            color: this.displayConfig.chartColor,
          },
          barWidth: `${this.displayConfig.barWidth || '50'}%`,
        },
      ],
    };
    this.barChartInstance.setOption(barOptions);
  }

  initLineChart() {
    this.lineChartInstance = echarts.init(this.lineChartRef.nativeElement, null, { renderer: 'svg' });
    const lineOptions: echarts.EChartsOption = {
      title: { text: this.displayConfig.chartTitle, left: 'center' },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: this.chartPreviewData![this.displayConfig.xAxisValue],
        name: this.displayConfig.xAxisLabel,
        nameLocation: 'middle',
        nameGap: this.xAxisFormatter(),
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 14,
          color: '#333',
        },
        splitLine: {
          show: this.displayConfig.xAxisGridlines,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          interval: 0,
          rotate: 30,
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        name: this.displayConfig.yAxisLabel,
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 14,
          color: '#333',
        },
        axisLabel: {
          formatter: this.numberFormatter,
        },
        splitLine: {
          show: this.displayConfig.yAxisGridlines,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
      },
      series: [
        {
          name: this.displayConfig.yAxisValue,
          type: 'line',
          data: this.chartPreviewData![this.displayConfig.yAxisValue],
          itemStyle: {
            color: this.displayConfig?.chartColor,
          },
        },
      ],
    };
    this.lineChartInstance.setOption(lineOptions);
  }

  initScatterChart() {
    const data = this.valueMappingForScatter(this.chartPreviewData!, [
      this.displayConfig.xAxisValue,
      this.displayConfig.yAxisValue,
    ]);

    this.scatterChartInstance = echarts.init(this.scatterChartRef.nativeElement, null, { renderer: 'svg' });
    const scatterOptions: echarts.EChartsOption = {
      title: { text: this.displayConfig.chartTitle, left: 'center' },
      tooltip: { trigger: 'axis' },
      xAxis: {
        name: this.displayConfig.xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 14,
          color: '#333',
        },
        axisLabel: {
          formatter: this.numberFormatter,
        },
        splitLine: {
          show: this.displayConfig.xAxisGridlines,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
      },
      yAxis: {
        name: this.displayConfig.yAxisLabel,
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: 14,
          color: '#333',
        },
        axisLabel: {
          formatter: this.numberFormatter,
        },
        splitLine: {
          show: this.displayConfig.yAxisGridlines,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
      },
      series: [
        {
          name: this.displayConfig.yAxisValue,
          symbolSize: this.displayConfig.pointSize,
          data: data,
          type: 'scatter',
          itemStyle: {
            color: this.displayConfig.chartColor,
          },
        },
      ],
    };
    this.scatterChartInstance.setOption(scatterOptions);
  }

  initPieChart() {
    /**
     * 
      | Placeholder | Refers to                    |
      | ----------- | ---------------------------- |
      | `{a}`       | Series name (`series.name`)  |
      | `{b}`       | Data item name (`data.name`) |
      | `{c}`       | Data value (`data.value`)    |
      | `{d}`       | Percentage (for pie, etc.)   |

     */
    const data = this.valueMappingForPie(
      this.chartPreviewData!,
      this.displayConfig.category,
      this.displayConfig.categoryValue,
    );

    this.pieChartInstance = echarts.init(this.pieChartRef.nativeElement, null, { renderer: 'svg' });
    const pieOptions: echarts.EChartsOption = {
      title: {
        text: this.displayConfig.chartTitle,
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
      },
      legend: {
        orient: 'vertical',
        left: 'left',
      },
      color: this.getColorPalettes(),
      series: [
        {
          name: this.displayConfig.categoryValue,
          type: 'pie',
          radius: '50%',
          data: data,
          label: {
            formatter: '{b} ({d}%)',
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
    this.pieChartInstance.setOption(pieOptions);
  }

  initGeoChart() {
    this.geoChartInstance = echarts.init(this.geoChartRef.nativeElement, null, { renderer: 'svg' });
    // This is to render the chart in correct height as this method uses fetch method, without set timeout it takes incorrect initial height
    setTimeout(() => {
      this.usaChoroplethMap();
    }, 500);
  }

  numberFormatter(value: number) {
    const absVal = Math.abs(value);
    let formatted: string;

    if (absVal >= 1_000_000_000_000) {
      formatted = (absVal / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + 'T';
    } else if (absVal >= 1_000_000_000) {
      formatted = (absVal / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    } else if (absVal >= 1_000_000) {
      formatted = (absVal / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (absVal >= 1_000) {
      formatted = (absVal / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    } else {
      formatted = absVal.toString();
    }

    return value < 0 ? '-' + formatted : formatted;
  }

  xAxisFormatter(): number {
    const xAxisValue = this.chartPreviewData![this.displayConfig.xAxisValue];
    const valueLength = xAxisValue[0].toString().length;
    let gap = `${valueLength - 1}0`;
    if (valueLength <= 3) {
      gap = `${valueLength}0`;
      return Number(gap);
    }
    if (valueLength > 5) {
      return 45;
    }
    return Number(gap);
  }

  async usaChoroplethMap() {
    const usaMap = await fetch('assets/files/usa.json').then((res) => res.json());
    echarts.registerMap('USA', usaMap, {
      Alaska: {
        left: -131,
        top: 25,
        width: 15,
      },
      Hawaii: {
        left: -110,
        top: 28,
        width: 5,
      },
      'Puerto Rico': {
        left: -76,
        top: 26,
        width: 2,
      },
    });

    const data = this.valueMappingForGeo(
      this.chartPreviewData!,
      this.displayConfig.location,
      this.displayConfig.locationValue,
    );
    const geoOptions: echarts.EChartsOption = {
      title: {
        text: this.displayConfig.chartTitle,
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
      },
      visualMap: {
        left: 'left',
        min: Math.min(...data.map((item: { value: number }) => item.value)),
        max: Math.max(...data.map((item: { value: number }) => item.value)),
        inRange: {
          color: this.getColorPalettes(),
        },
        text: ['High', 'Low'],
        calculable: true,
      },
      series: [
        {
          name: this.displayConfig.locationValue,
          type: 'map',
          map: 'USA',
          roam: true,
          zoom: 1,
          scaleLimit: {
            min: 0.8,
            max: 5,
          },
          emphasis: {
            label: {
              show: true,
            },
          },
          data: data,
        },
      ],
    };
    this.geoChartInstance.setOption(geoOptions);
  }

  resizeCharts = () => {
    this.barChartInstance?.resize();
    this.lineChartInstance?.resize();
    this.scatterChartInstance?.resize();
    this.pieChartInstance?.resize();
    this.geoChartInstance?.resize();
  };

  private renderChart(): void {
    switch (this.type) {
      case ChartType.BAR:
        this.barChartInstance?.dispose();
        this.initBarChart();
        break;
      case ChartType.LINE:
        this.lineChartInstance?.dispose();
        this.initLineChart();
        break;
      case ChartType.PIE:
        this.pieChartInstance?.dispose();
        this.initPieChart();
        break;
      case ChartType.SCATTER:
        this.scatterChartInstance?.dispose();
        this.initScatterChart();
        break;
      case ChartType.GEO:
        this.geoChartInstance?.dispose();
        this.initGeoChart();
        break;
    }
  }

  generateChartImage(chartType: string) {
    const chartInstance: echarts.ECharts = this.getActiveChartInstance(chartType);
    const base64Image = chartInstance.getDataURL({
      type: 'svg',
      pixelRatio: 4,
      backgroundColor: '#fff',
    });
    this.chartSaved.emit(base64Image);
  }

  getActiveChartInstance(chartType: string): echarts.ECharts {
    let instance: echarts.ECharts;
    switch (chartType) {
      case ChartType.BAR:
        instance = this.barChartInstance;
        break;
      case ChartType.LINE:
        instance = this.lineChartInstance;
        break;
      case ChartType.PIE:
        instance = this.pieChartInstance;
        break;
      case ChartType.SCATTER:
        instance = this.scatterChartInstance;
        break;
      case ChartType.GEO:
        instance = this.geoChartInstance;
        break;
    }

    return instance!;
  }

  resetChart() {
    const chartInstance = this.getActiveChartInstance(this.type!);
    if (chartInstance) {
      chartInstance.dispatchAction({
        type: 'restore',
      });
    }
  }

  private valueMappingForScatter(data: Record<string, any[]>, keys: string[]): any[][] {
    {
      if (keys.length === 0) return [];

      const length = data[keys[0]].length;
      return Array.from({ length }, (_, i) => keys.map((k) => data[k][i]));
    }
  }

  private valueMappingForPie(data: DataArrays, nameKey: string, valueKey: string): { name: string; value: number }[] {
    const names = data[nameKey] as string[];
    const values = data[valueKey] as number[];

    if (!names || !values || names.length !== values.length) {
      throw new Error('Arrays must exist and have the same length');
    }

    const resultArray = names.map((name, i) => {
      const value = values[i] ? Number(values[i]) : values[i];
      const Obj = {
        name: String(name),
        value: value,
      };
      return Obj;
    });
    return resultArray;
  }

  private valueMappingForGeo(
    data: DataArrays,
    locationKey: string,
    valueKey: string,
  ): { name: string; value: number }[] {
    const locations = data[locationKey] as string[];
    const values = data[valueKey] as number[];
    const isValidData = locations && values && locations.length === values.length;
    if (!isValidData) {
      this.nzMessageService.error('Invalid data for Geo chart.');
      throw new Error('Invalid data for Geo chart.');
    }
    if (!locations || !values || locations.length !== values.length) {
      throw new Error('Arrays must exist and have the same length');
    }
    const usStates = Object.values(US_STATE_MAPPINGS);
    const resultArray = locations
      .map((location: string, i) => {
        const name =
          location.length == 2
            ? US_STATE_MAPPINGS[location.toUpperCase()]
            : location.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

        if (!usStates.includes(name)) return undefined;
        const value = values[i] ? Number(values[i]) : values[i];
        return {
          name: String(name),
          value: value,
        };
      })
      .filter((item) => item !== undefined);

    if (resultArray.length < 1) {
      this.nzMessageService.error('No valid US state data found for Geo chart.');
      throw new Error('No valid US state data found for Geo chart.');
    }
    return resultArray;
  }

  getColorPalettes(): string[] {
    const palette = COLOR_PALETTES.find((p) => p.id === this.displayConfig.colorPalette);
    return palette ? palette.colors : COLOR_PALETTES[0].colors;
  }
}
